import { useTranslation } from 'react-i18next'
import { Breadcrumbs, Button, LinearProgress, ListItemButton, Typography } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useContext, useState, useRef } from 'react'

import { config } from '../config'
import { countriesCurrencies } from '../lib'
import Dialog from '../components/common/dialog'
import NextIcon from '../assets/icons/next-icon.svg'
import { requestAccessToken } from '../api/auth.api'
import HelpIcon from '../assets/icons/help-icon.svg'
import { deletePaymentData, downloadPaymentVoucher } from '../api/payments.api'
import Pagination from '../components/common/pagination'
import { getPaymentsRejectedData } from '../api/app-data.api'
import { Customer, FormError, Payment } from '../types'
import { FiltersOptionsModal } from '../components/common/filters-options-modal'
import { ActionsBar } from '../components/payments-to-review-management/actions-bar'
import { handleDeleteDataByOne } from '../utils/db/common-methods'
import { AppStateContext, AuthContext, NotificationsContext, UserContext } from '../contexts'
import { PaymentRejectedListItem } from '../components/payments-rejected-management/payment-rejected-list-item'
import { normalizePaymentData, handleSyncDataToLocalDB, upsertManyWithLimitAtStartFast, upsertPaymentWithLimitAtStart } from '../utils'
import CustomerDetailsPaymentDetailsDataTable from '../components/customer-details-management/customer-details-payment-details-data-table'
import { baseButtonBlueBg } from '../theme/custom-styles'
import { searchPaymentsByRegex } from '../api/app-queries.api'
import { useSocket } from '../hooks/use-socket'

const PaymentsRejectedManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const location = useLocation()
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { setScrollToTop, processingBatch } = useContext(AppStateContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t } = useTranslation(['payments-to-review-management', 'customer-details-management', 'common'])

  const currentListPageRef = useRef<number>(1)
  const paymentToSearchRef = useRef<string>('')
  const isComponentMount = useRef<boolean>(false)
  const currentLoadingDataRef = useRef<boolean>(true)
  const paymentsToDeleteRef = useRef<Array<string>>([])
  const paymentStatusAllowedRef = useRef<Array<string>>([])
  const noRegistriesFoundAtDatabaseRef = useRef<boolean>(false)
  const currentShowDeleteButtonFromActionsBarRef = useRef<boolean>(false)
  const currentPaymentsDataRef = useRef<Array<Payment>>([])

  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [paymentToSearch, setPaymentToSearch] = useState<string>('')
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [customersData, setCustomersData] = useState<Array<Customer>>([])
  const [selectAllPayments, setSelectAllPayments] = useState<boolean>(false)
  const [paymentsToDelete, setPaymentsToDelete] = useState<Array<string>>([])
  const [paymentsList, setPaymentsList] = useState<Array<Partial<Payment>>>([])
  const [paymentsData, setPaymentsData] = useState<Array<Partial<Payment>>>([])
  const [searchingInDatabase, setSearchingInDatabase] = useState<boolean>(false)
  const [openDeletePaymentModal, setOpenDeletePaymentModal] = useState<boolean>(false)
  const [errorInDeletionProcess, setErrorInDeletionProcess] = useState<boolean>(false)
  const [paymentSelectedData, setPaymentSelectedData] = useState<Partial<Payment>>({})
  const [searchedPayments, setSearchedPayments] = useState<Array<Partial<Payment>>>([])
  const [openFiltersOptionsModal, setOpenFiltersOptionsModal] = useState<boolean>(false)
  const [paymentDetailsIsLoading, setPaymentDetailsIsLoading] = useState<boolean>(false)
  const [openPaymentDetailsModal, setOpenPaymentDetailsModal] = useState<boolean>(false)
  const [noRegistriesFoundAtLocal, setNoRegistriesFoundAtLocal] = useState<boolean>(false)
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState<Partial<Payment>>({})
  const [noRegistriesFoundAtDatabase, setNoRegistriesFoundAtDatabase] = useState<boolean>(false)
  const [dialogDeletePaymentIsLoading, setDialogDeletePaymentIsLoading] = useState<boolean>(false)
  const [showDeleteButtonFromActionsBar, setShowDeleteButtonFromActionsBar] = useState<boolean>(false)

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    paymentStatusAllowedRef.current = location.pathname.includes('payments-returned') ? ['returned'] : ['rejected', 'error']

    appDataSocket.on(`newPaymentAdded:${selectedCurrency}`, async (dataFromSocket: any) => {
      if (!paymentStatusAllowedRef.current.includes(dataFromSocket.status)) {
        handleDeleteAndSetPayments(String(dataFromSocket._id))
        return
      }

      handleUpdateAndSetPayments(dataFromSocket)
    })

    appDataSocket.on(`paymentUpdated:${selectedCurrency}`, async (dataFromSocket: any) => {
      if (!paymentStatusAllowedRef.current.includes(dataFromSocket.status) || (paymentStatusAllowedRef.current.includes(dataFromSocket.status) && dataFromSocket.deleted === true)) {
        handleDeleteAndSetPayments(String(dataFromSocket._id))
        return
      }

      handleUpdateAndSetPayments(dataFromSocket)
    })

    appDataSocket.on(`paymentDeleted:${selectedCurrency}`, async (dataFromSocket: any) => {
      if (paymentStatusAllowedRef.current.includes(dataFromSocket.status)) {
        handleDeleteAndSetPayments(String(dataFromSocket.paymentId))
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    handleGetPaymentsRejected()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (paymentsToDelete.length === 0) {
      currentShowDeleteButtonFromActionsBarRef.current = false
      setShowDeleteButtonFromActionsBar(false)
    } else {
      if (!paymentSelectedData.customerData?.registeredFullName) {
        currentShowDeleteButtonFromActionsBarRef.current = true
        setShowDeleteButtonFromActionsBar(true)
      }
    }
  }, [paymentsToDelete, paymentSelectedData])

  useEffect(() => {
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const paymentsPerPage = paymentToSearch === '' ? paymentsData.slice(firstPageIndex, lastPageIndex) : searchedPayments.slice(firstPageIndex, lastPageIndex)

    setPaymentsList(paymentsPerPage)

    if (processingBatch !== true) {
      for (const element of paymentsPerPage) {
        const paymentToAdd = normalizePaymentData(element)
        handleSyncDataToLocalDB('payments', paymentToAdd)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, paymentsData, searchedPayments])

  const handleDeleteAndSetPayments = (paymentId: string) => {
    const paymentsDataUpdated = currentPaymentsDataRef.current.filter((paymentData: Payment) => String(paymentData._id) !== String(paymentId))
    globalThis.sessionStorage.setItem(`paymentsRejected:${selectedCountry}`, JSON.stringify({ paymentsData: paymentsDataUpdated }))

    handleSetInitialData({ paymentsData: paymentsDataUpdated })
  }

  const handleUpdateAndSetPayments = (paymentData: Payment) => {
    const localPaymentsCompletedData = globalThis.sessionStorage.getItem(`paymentsRejected:${selectedCountry}`) || JSON.stringify({ paymentsData: [] })
    const paymentsDataParsed = JSON.parse(localPaymentsCompletedData)
    const paymentsDataUpdated = upsertPaymentWithLimitAtStart(paymentsDataParsed.paymentsData, paymentData, 500)

    handleSetInitialData({ paymentsData: paymentsDataUpdated })
  }

  const handleGetPaymentsRejected = async () => {
    let paymentsCompletedDataToSet: any = {}
    const localPaymentsCompletedData = globalThis.sessionStorage.getItem(`paymentsRejected:${selectedCountry}`)

    if (localPaymentsCompletedData) {
      paymentsCompletedDataToSet = JSON.parse(localPaymentsCompletedData)
      handleSetInitialData(paymentsCompletedDataToSet)
    }

    handleGetPaymentsRejectedFromRemote()
  }

  const handleGetPaymentsRejectedFromRemote = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getPaymentsRejectedData({ selectedCountry, selectedCurrency }, accessToken)

      handleSetInitialData(data)

      globalThis.sessionStorage.setItem(`paymentsRejected:${selectedCountry}`, JSON.stringify(data))
    } catch (error) {
      console.log('Error handleGetPaymentsCompletedFromRemote > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    const paymentsToDisplay = location.pathname.includes('payments-returned')
      ? dataToSet.paymentsData.filter((paymentData: Payment) => paymentData.status === 'returned')
      : dataToSet.paymentsData.filter((paymentData: Payment) => paymentData.status !== 'returned')

    if (paymentsToDisplay.length === 0 && currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      currentPaymentsDataRef.current = []
      setLoadingLocalData(false)
      return
    }

    const currentSelectedPaymentsIds =
      currentPaymentsDataRef.current.length === 0 ? [] : currentPaymentsDataRef.current.filter((paymentData) => paymentData.selected === true).map((paymentData) => String(paymentData._id))
    const currentListPageDataState = currentListPageRef.current
    const currentPaymentToSearchState = paymentToSearchRef.current
    const paymentsNormalizedData = []

    for (const element of paymentsToDisplay) {
      const paymentData = { ...element }
      const paymentDataNormalized = normalizePaymentData(paymentData) as Payment

      if (paymentDataNormalized.customerId && !paymentDataNormalized.customerRegisteredFullName) {
        paymentDataNormalized.customerData = {
          registeredFullName: t('empty')
        }
      } else {
        paymentDataNormalized.customerData = paymentData.customerData
      }

      if (currentSelectedPaymentsIds.includes(String(paymentDataNormalized._id))) {
        paymentDataNormalized.selected = true
      } else {
        paymentDataNormalized.selected = false
      }

      paymentsNormalizedData.push(paymentDataNormalized)
    }

    paymentsNormalizedData.sort((thisPayment: Partial<Payment>, nextPayment: Partial<Payment>) => {
      return Date.parse(String(nextPayment.createdAt)) - Date.parse(String(thisPayment.createdAt))
    })

    if (currentPaymentToSearchState === '' || currentPaymentToSearchState === null) {
      const firstPageIndex = currentListPageDataState === null ? 0 : (currentListPageDataState - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const paymentsPerPage = paymentsNormalizedData.slice(firstPageIndex, lastPageIndex)

      handlePaymentsDeletionProcess(paymentsPerPage)
      setPaymentsList(paymentsPerPage)

      if (processingBatch !== true) {
        for (const element of paymentsPerPage) {
          const paymentToAdd = normalizePaymentData(element)
          handleSyncDataToLocalDB('payments', paymentToAdd)
        }
      }
    }

    currentPaymentsDataRef.current = paymentsNormalizedData
    setPaymentsData(paymentsNormalizedData)
    setCustomersData(dataToSet.customersData)

    if (currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      setLoadingLocalData(false)
    }

    if (isComponentMount.current === false) {
      isComponentMount.current = true

      if (location.state?.searchFilter) {
        handleSetPaymentToSearch(location.state?.searchFilter, true)
      }
    }
  }

  const handlePaymentsDeletionProcess = (paymentsPerPage: Array<Payment>) => {
    const currentPaymentsToDeleteState = paymentsToDeleteRef.current ?? []
    const currentShowDeleteButtonFromActionsBarState = currentShowDeleteButtonFromActionsBarRef.current
    const paymentsToDeleteOutOfPage = []

    for (const element of currentPaymentsToDeleteState) {
      const paymentToDeleteId = element
      const findCustomerIndex = paymentsPerPage?.findIndex((paymentData) => String(paymentData._id) === String(paymentToDeleteId))

      if (findCustomerIndex === -1) {
        paymentsToDeleteOutOfPage.push(paymentToDeleteId)
      }
    }

    for (const element of paymentsToDeleteOutOfPage) {
      const customerId = element
      const index = currentPaymentsToDeleteState.indexOf(customerId)
      if (index > -1) {
        currentPaymentsToDeleteState.splice(index, 1)
      }
    }

    paymentsToDeleteRef.current = currentPaymentsToDeleteState
    if (paymentsToDeleteRef.current.length < pageSize) {
      setSelectAllPayments(false)
    }

    if (paymentsToDeleteRef.current.length === 0) {
      setShowDeleteButtonFromActionsBar(false)
    } else {
      setShowDeleteButtonFromActionsBar(currentShowDeleteButtonFromActionsBarState)
    }

    setTimeout(() => {
      setPaymentsToDelete(currentPaymentsToDeleteState)
    }, 50)
  }

  const handleUncheckAllPayments = () => {
    setSelectAllPayments(false)
    setPaymentsData((currentPaymentsState) => {
      const paymentsStateUpdated = [...currentPaymentsState]
      const paymentsStateUpdatedToReturn = paymentsStateUpdated.map((paymentData) => ({
        ...paymentData,
        selected: false
      }))

      currentPaymentsDataRef.current = paymentsStateUpdatedToReturn as Array<Payment>
      return paymentsStateUpdatedToReturn
    })
  }

  const handleSetPaymentToSearch = (paymentToSearchData: string, showError: boolean) => {
    currentListPageRef.current = 1
    paymentToSearchRef.current = paymentToSearchData

    if (noRegistriesFoundAtDatabaseRef.current === true) {
      setNoRegistriesFoundAtDatabase(false)
    }

    setErrors({})
    setCurrentListPage(1)
    setNoRegistriesFoundAtLocal(false)
    setPaymentToSearch(paymentToSearchData)

    if (paymentToSearchData === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentPaymentsDataState = currentPaymentsDataRef.current ?? []
      const paymentsPerPage = currentPaymentsDataState.slice(firstPageIndex, lastPageIndex)

      setPaymentsList(paymentsPerPage)
      setSearchedPayments([])

      if (showError) setErrors({ paymentToSearch: t('searchField.errors.emptyField') })
      return
    }

    const currentFullPaymentsData = [...(currentPaymentsDataRef.current as Array<Payment>)]
    const filteredPayments = currentFullPaymentsData.filter((paymentData) => {
      const paymentStatus =
        paymentData.status.indexOf('error') !== -1
          ? t('paymentStatus.error', { ns: 'customer-details-management' })
          : paymentData.status.indexOf('returned') !== -1
          ? t('paymentStatus.returned', { ns: 'customer-details-management' })
          : t('paymentStatus.rejected', { ns: 'customer-details-management' })

      const customerRegisteredFullName = paymentData.customerData?.registeredFullName ? paymentData.customerData.registeredFullName : ''
      const customerDocumentNumber = paymentData.customerData?.documentNumber ? paymentData.customerData.documentNumber : ''
      const paymentReferenceNumber = paymentData.referenceNumber

      const customerBanksAccountsData = Array.isArray(paymentData.customerData?.bankAccountsData)
        ? paymentData.customerData?.bankAccountsData.filter((bankAccountData) => {
            return bankAccountData.bankNumber.toLowerCase().includes(paymentToSearchData.toLowerCase())
          })
        : []

      return (
        paymentStatus.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        customerRegisteredFullName.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        customerDocumentNumber.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        paymentReferenceNumber.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        customerBanksAccountsData.length > 0
      )
    })

    paymentsToDeleteRef.current = []
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const paymentsPerPage = filteredPayments.slice(firstPageIndex, lastPageIndex)

    setPaymentsToDelete([])
    handleUncheckAllPayments()
    setPaymentsList(paymentsPerPage)
    setSearchedPayments(filteredPayments)

    if (filteredPayments.length === 0) {
      setNoRegistriesFoundAtLocal(true)
    }
  }

  const handleConfirmPaymentDeletion = async () => {
    setErrors({})
    setDialogDeletePaymentIsLoading(true)
    const currentPaymentsToDelete = [...paymentsToDeleteRef.current]

    for (const element of currentPaymentsToDelete) {
      const paymentToDeleteId = element

      if (processingBatch === true) {
        handleDiscardDeletionProcess()
        setOpenDeletePaymentModal(false)
        updateNewNotificationStatus?.(true, 'deletion-process-interrupted')
        break
      }

      try {
        const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
        await deletePaymentData(paymentToDeleteId, accessTokenData.data.accessToken)
        await handleDeleteDataByOne('payments', paymentToDeleteId)
      } catch (error: any) {
        console.log(error, error?.response?.data?.message)
        if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
          handleCloseSession()
        } else if (error?.response?.status === 401) {
          setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
        } else {
          setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
        }

        // Es necesario dejar esto aquí
        handleDiscardDeletionProcess()
        setErrorInDeletionProcess(true)
        return
      }
    }

    handleDiscardDeletionProcess()
    setOpenDeletePaymentModal(false)
    updateNewNotificationStatus?.(true, 'payment-deleted')
  }

  const handleDiscardDeletionProcess = () => {
    setPaymentsToDelete([])
    setPaymentSelectedData({})
    handleUncheckAllPayments()
    paymentsToDeleteRef.current = []
    setDialogDeletePaymentIsLoading(false)
    const currentPaymentToSearchState = paymentToSearchRef.current

    if (currentPaymentToSearchState !== '') {
      handleSetPaymentToSearch('', false)
    }
  }

  const handleDownloadPaymentVoucher = async () => {
    try {
      setErrors({})
      setPaymentDetailsIsLoading(true)
      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const paymentVoucherDownloaded = await downloadPaymentVoucher(String(selectedPaymentDetails._id), accessTokenData.data.accessToken)

      window.open(paymentVoucherDownloaded.data, '_blank')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }
    }

    setPaymentDetailsIsLoading(false)
  }

  const handleSearchPaymentsRejectedDataByRegex = async () => {
    try {
      setErrors({})
      setSearchingInDatabase(true)

      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))

      const { data } = await searchPaymentsByRegex({ searchText: paymentToSearch, status: 'rejected', belongsTo: userSession?.belongsTo, selectedCountry, selectedCurrency }, accessToken)
      const paymentsToDisplay = location.pathname.includes('payments-returned')
        ? data.paymentsData.filter((paymentData: Payment) => paymentData.status === 'returned')
        : data.paymentsData.filter((paymentData: Payment) => paymentData.status !== 'returned')

      if (paymentsToDisplay.length === 0) {
        setSearchingInDatabase(false)
        setNoRegistriesFoundAtDatabase(true)
        noRegistriesFoundAtDatabaseRef.current = true
        return
      }

      /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */
      paymentsToDisplay.sort((thisDocument: Partial<Payment>, nextDocument: Partial<Payment>) => {
        return Date.parse(String(nextDocument.createdAt)) - Date.parse(String(thisDocument.createdAt))
      })
      setPaymentsList(paymentsToDisplay)
      setSearchedPayments(paymentsToDisplay)
      /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */

      const currentPaymentsData = [...currentPaymentsDataRef.current]
      const paymentsDataUpdated = upsertManyWithLimitAtStartFast(currentPaymentsData, paymentsToDisplay, 1000)
      paymentsDataUpdated.sort((thisDocument: Partial<Payment>, nextDocument: Partial<Payment>) => {
        return Date.parse(String(nextDocument.createdAt)) - Date.parse(String(thisDocument.createdAt))
      })

      currentPaymentsDataRef.current = paymentsDataUpdated
      setPaymentsData(paymentsDataUpdated)

      setSearchingInDatabase(false)
      setNoRegistriesFoundAtLocal(false)

      globalThis.sessionStorage.setItem(`paymentsRejected:${selectedCountry}`, JSON.stringify({ paymentsData: data.paymentsData }))
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setSearchingInDatabase(false)
      return
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div className="section-container">
      <Breadcrumbs
        separator={
          <img
            src={NextIcon}
            alt="next-icon"
          />
        }
        aria-label="breadcrumb"
      >
        {userSession &&
        (userSession.role === 'super-admin-client' || userSession.role === 'admin-client') &&
        location.state?.previousPath &&
        location.state?.previousPath === `customer-company-payments/${userSession.belongsTo}` ? (
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate('/')}
          >
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {t('breadcrumbs.home')}
            </Typography>
          </ListItemButton>
        ) : (
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.firstLevel')}
          </Typography>
        )}
        {userSession &&
          (userSession.role === 'super-admin-client' || userSession.role === 'admin-client') &&
          location.state?.previousPath &&
          location.state?.previousPath === `customer-company-payments/${userSession.belongsTo}` && (
            <ListItemButton
              sx={{
                '&.MuiButtonBase-root': {
                  padding: '0px'
                },
                '&.MuiButtonBase-root:hover': {
                  backgroundColor: 'rgba(0,0,0,0)'
                }
              }}
              onClick={() => navigate(`/customer-company-payments/${userSession.belongsTo}`)}
            >
              <Typography
                key="3"
                className="breadcrumb-inactive"
              >
                {t('breadcrumbs.individualPayments')}
              </Typography>
            </ListItemButton>
          )}
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {location.pathname.includes('returned') ? t('breadcrumbs.returnedLevel') : t('breadcrumbs.rejectedLevel')}
        </Typography>
      </Breadcrumbs>
      <ActionsBar
        errors={errors}
        paymentsData={paymentsData}
        paymentToSearch={paymentToSearch}
        processingDocument={processingBatch}
        className="payments-rejected-app-bar"
        generalActionChecked={selectAllPayments}
        searchingInDatabase={searchingInDatabase}
        handleClearSearchInput={() => setErrors({})}
        showDeleteButton={showDeleteButtonFromActionsBar}
        handleSetPaymentToSearch={handleSetPaymentToSearch}
        handleOpenDeletePaymentModal={() => setOpenDeletePaymentModal(true)}
        handleOpenFiltersOptionsModal={() => setOpenFiltersOptionsModal(true)}
        handleToggleGeneralActionChecked={(checked: boolean) => {
          setSelectAllPayments(checked)

          if (checked === true) {
            const currentPaymentsList = [...paymentsList]
            const currentPaymentsDataStateUpdated = currentPaymentsDataRef.current
            const paymentsListUpdated = currentPaymentsList.map((paymentData) => ({
              ...paymentData,
              selected: true
            }))
            const currentPaymentsDataIds = paymentsListUpdated.map((paymentData) => paymentData._id)
            const filteredPaymentsToDelete = currentPaymentsDataIds.filter((id) => id !== undefined) as string[]

            for (const element of paymentsListUpdated) {
              const paymentListData = element
              const findPaymentIndex = currentPaymentsDataStateUpdated?.findIndex((paymentData) => String(paymentData._id) === String(paymentListData._id)) ?? -1

              if (findPaymentIndex !== -1 && currentPaymentsDataStateUpdated) {
                currentPaymentsDataStateUpdated[findPaymentIndex].selected = true
              }
            }

            setPaymentsList(paymentsListUpdated)
            setShowDeleteButtonFromActionsBar(true)
            setPaymentsToDelete(filteredPaymentsToDelete)
            setPaymentsData(currentPaymentsDataStateUpdated as Array<Payment>)

            paymentsToDeleteRef.current = filteredPaymentsToDelete
            currentShowDeleteButtonFromActionsBarRef.current = true
            currentPaymentsDataRef.current = currentPaymentsDataStateUpdated
          } else {
            const currentPaymentsList = [...paymentsList]
            const currentPaymentsDataStateUpdated = currentPaymentsDataRef.current
            const paymentsListUpdated = currentPaymentsList.map((paymentData) => ({
              ...paymentData,
              selected: false
            }))

            for (const element of paymentsListUpdated) {
              const paymentListData = element
              const findPaymentIndex = currentPaymentsDataStateUpdated?.findIndex((paymentData) => String(paymentData._id) === String(paymentListData._id)) ?? -1

              if (findPaymentIndex !== -1 && currentPaymentsDataStateUpdated) {
                currentPaymentsDataStateUpdated[findPaymentIndex].selected = false
              }
            }

            handleUncheckAllPayments()
            setPaymentsToDelete([])
            setPaymentsList(paymentsListUpdated)
            setShowDeleteButtonFromActionsBar(false)
            setPaymentsData(currentPaymentsDataStateUpdated as Array<Payment>)

            paymentsToDeleteRef.current = []
            currentShowDeleteButtonFromActionsBarRef.current = false
            currentPaymentsDataRef.current = currentPaymentsDataStateUpdated
          }
        }}
      />
      <div className="table-header-container">
        <div className="customer-company-table-column customer-company-table-width-15 display-none-xlg table-header-text">{t('tableHeaders.date')}</div>
        <div className="customer-company-table-column customer-company-table-width-45 table-header-text customer-company-table-width-70-md-down">{t('tableHeaders.name')}</div>
        <div className="customer-company-table-column customer-company-table-width-45 table-header-text customer-company-table-width-30-md-down">{t('tableHeaders.status')}</div>
      </div>
      {loadingLocalData ? (
        <div className="documents-list-container">
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
        </div>
      ) : (
        <>
          <div className="documents-list-container">
            {paymentsList.map((paymentData: Partial<Payment>) => {
              return (
                <PaymentRejectedListItem
                  key={paymentData._id}
                  paymentData={paymentData}
                  processingDocument={processingBatch}
                  handleGoToPaymentDetails={(selectedPaymentData: Partial<Payment>) => {
                    setOpenPaymentDetailsModal(true)
                    setSelectedPaymentDetails(selectedPaymentData)
                  }}
                  handleTogglePaymentSelection={(selectedPaymentData: Partial<Payment>, selected: boolean) => {
                    let paymentsSelected = 0
                    let paymentsToDeleteUpdated = []
                    const currentPaymentsList = [...paymentsList]
                    const currentPaymentsToDelete = [...paymentsToDeleteRef.current]
                    const currentPaymentsDataStateUpdated = currentPaymentsDataRef.current

                    const paymentsListUpdated = currentPaymentsList.map((paymentData) => {
                      if (String(paymentData._id) === String(selectedPaymentData._id)) {
                        paymentData.selected = selected
                      }

                      if (paymentData.selected === true) paymentsSelected++

                      return paymentData
                    })

                    if (selected === true) {
                      paymentsToDeleteUpdated = paymentsToDeleteRef.current.concat(selectedPaymentData._id ?? [])
                      paymentsToDeleteRef.current = paymentsToDeleteUpdated
                      setPaymentsToDelete(paymentsToDeleteUpdated)

                      const findPaymentIndex = currentPaymentsDataStateUpdated?.findIndex((paymentData) => String(paymentData._id) === String(selectedPaymentData._id)) ?? -1

                      if (findPaymentIndex !== -1 && currentPaymentsDataStateUpdated) {
                        currentPaymentsDataStateUpdated[findPaymentIndex].selected = true
                      }

                      currentPaymentsDataRef.current = currentPaymentsDataStateUpdated
                      setPaymentsData(currentPaymentsDataStateUpdated as Array<Payment>)
                    } else {
                      const index = currentPaymentsToDelete.indexOf(selectedPaymentData._id ?? '')
                      if (index > -1) {
                        currentPaymentsToDelete.splice(index, 1)
                      }

                      paymentsToDeleteUpdated = [...currentPaymentsToDelete]
                      paymentsToDeleteRef.current = paymentsToDeleteUpdated
                      setPaymentsToDelete(currentPaymentsToDelete)

                      const findPaymentIndex = currentPaymentsDataStateUpdated?.findIndex((paymentData) => String(paymentData._id) === String(selectedPaymentData._id)) ?? -1

                      if (findPaymentIndex !== -1 && currentPaymentsDataStateUpdated) {
                        currentPaymentsDataStateUpdated[findPaymentIndex].selected = false
                      }

                      currentPaymentsDataRef.current = currentPaymentsDataStateUpdated
                      setPaymentsData(currentPaymentsDataStateUpdated as Array<Payment>)
                    }

                    if (paymentsToDeleteUpdated.length > 0) {
                      currentShowDeleteButtonFromActionsBarRef.current = true
                      setShowDeleteButtonFromActionsBar(true)
                    } else {
                      currentShowDeleteButtonFromActionsBarRef.current = false
                      setShowDeleteButtonFromActionsBar(false)
                    }

                    if (paymentsList.length === paymentsSelected) {
                      setSelectAllPayments(true)
                    } else {
                      setSelectAllPayments(false)
                    }

                    setPaymentsList(paymentsListUpdated)
                  }}
                  /* handleOpenDeletePaymentDataModal={(selectedPaymentData: Partial<Payment>) => {
                    const paymentsToDeleteUpdated = [selectedPaymentData._id] as Array<string>
                    const currentPaymentsList = [...paymentsList]
                    const paymentsListUpdated = currentPaymentsList.map((paymentData) => ({
                      ...paymentData,
                      selected: false
                    }))

                    setPaymentSelectedData({
                      ...selectedPaymentData
                    })
                    handleUncheckAllPayments()
                    setSelectAllPayments(false)
                    setOpenDeletePaymentModal(true)
                    setPaymentsList(paymentsListUpdated)
                    setShowDeleteButtonFromActionsBar(false)
                    setPaymentsToDelete(paymentsToDeleteUpdated)

                    paymentsToDeleteRef.current = paymentsToDeleteUpdated
                    currentShowDeleteButtonFromActionsBarRef.current = false
                  }} */
                />
              )
            })}
          </div>
          <Pagination
            pageSize={pageSize}
            currentPage={currentListPage}
            handlePaginationPageChange={(page: number) => {
              handleUncheckAllPayments()

              setTimeout(() => {
                setScrollToTop(true)
                setPaymentsToDelete([])
                setCurrentListPage(page)
                setSelectAllPayments(false)

                paymentsToDeleteRef.current = []
                currentListPageRef.current = page
              }, 100)
            }}
            handleChangePageSize={(pageSizeToSet: string) => {
              setScrollToTop(true)
              setPageSize(Number(pageSizeToSet))
            }}
            totalCount={paymentToSearch === '' ? paymentsData.length : searchedPayments.length}
          />
          {noRegistriesFoundAtLocal && (
            <div className="customer-no-data-found-container">
              {searchingInDatabase ? (
                <>
                  <div className="customer-no-data-found-searching-progress-bar">
                    <LinearProgress />
                  </div>
                  <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case3', { ns: 'customer-company-details-management' })}</div>
                </>
              ) : (
                <>
                  {noRegistriesFoundAtDatabase === false ? (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        {t('searchingResultTexts.case1.firstPart', { ns: 'customer-company-details-management' })}{' '}
                        <strong
                          className="customer-no-data-found-no-registries-text-click"
                          onClick={() => handleSearchPaymentsRejectedDataByRegex()}
                        >
                          {t('searchingResultTexts.case1.thirdPart', { ns: 'customer-company-details-management' })}
                        </strong>{' '}
                        {t('searchingResultTexts.case1.fourthPart', { ns: 'customer-company-details-management' })}
                      </div>
                      <Button
                        sx={baseButtonBlueBg}
                        onClick={handleSearchPaymentsRejectedDataByRegex}
                      >
                        {t('searchingResultTexts.expandSearch', { ns: 'customer-company-details-management' })}
                      </Button>
                      {errors.generalError && (
                        <div
                          style={{ marginTop: '0px' }}
                          className="error-message"
                        >
                          {errors.generalError}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        <img
                          src={HelpIcon}
                          alt="help-icon"
                        />
                      </div>
                      <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case2', { ns: 'customer-company-details-management' })}</div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
          {openPaymentDetailsModal && (
            <Dialog
              errors={errors}
              dialogIsLoading={paymentDetailsIsLoading}
              hideActionsButtons={
                userSession?.role?.includes('-builder') ||
                selectedPaymentDetails.status === 'reviewed' ||
                selectedPaymentDetails.status === 'error' ||
                selectedPaymentDetails.status === 'processing' ||
                selectedPaymentDetails.status === 'rejected' ||
                (userSession?.role === 'admin-client' && userSession?.belongsTo === config.app.isliveCompanyId)
              }
              dialogTitle={t('paymentDetailsModal.title', { ns: 'document-payments-details-management' })}
              handleDialogAction={() => handleDownloadPaymentVoucher()}
              handleCloseOptionAction={() => {
                setErrors({})
                setOpenPaymentDetailsModal(false)
                setSelectedPaymentDetails({})
              }}
              handleDialogOpenState={(openState: boolean) => {
                setErrors({})
                setOpenPaymentDetailsModal(openState)
                setSelectedPaymentDetails({})
              }}
              actionButtonText={
                selectedPaymentDetails.status === 'paid' || selectedPaymentDetails.status === 'validated' || selectedPaymentDetails.status === 'returned'
                  ? t('paymentDetailsModal.actionButtonText', { ns: 'document-payments-details-management' })
                  : t('paymentDetailsModal.sendActionButtonText', { ns: 'document-payments-details-management' })
              }
            >
              <CustomerDetailsPaymentDetailsDataTable paymentDetailsData={selectedPaymentDetails} />
            </Dialog>
          )}
          {openDeletePaymentModal && (
            <Dialog
              errors={errors}
              actionButtonWarningStyle
              hideActionsButtons={errorInDeletionProcess}
              dialogIsLoading={dialogDeletePaymentIsLoading}
              handleDialogAction={handleConfirmPaymentDeletion}
              actionButtonText={t('deletePaymentModal.actionButtonText')}
              dialogTitle={paymentsToDelete.length === 1 ? t('deletePaymentModal.simpleTitle') : t('deletePaymentModal.multipleTitle')}
              handleDialogOpenState={(openState: boolean) => {
                setOpenDeletePaymentModal(openState)

                if (openState === false) {
                  setErrors({})
                  setErrorInDeletionProcess(false)
                  setPaymentSelectedData((currentSelectedPaymentState: Partial<Payment>) => {
                    if (currentSelectedPaymentState.customerData?.registeredFullName) {
                      setPaymentsToDelete([])
                      paymentsToDeleteRef.current = []
                    }

                    return {}
                  })
                }
              }}
            >
              <div className="dialog-content-text">
                {paymentSelectedData.customerData?.registeredFullName
                  ? t('deletePaymentModal.simpleText', { registeredFullName: paymentSelectedData.customerData?.registeredFullName.toUpperCase() })
                  : t('deletePaymentModal.multipleText')}
              </div>
            </Dialog>
          )}
        </>
      )}
      {openFiltersOptionsModal && (
        <FiltersOptionsModal
          filtersToApply="payments"
          showNoveltyTypeFilter={false}
          customersData={customersData}
          handleDialogOpenState={(openState: boolean) => {
            setOpenFiltersOptionsModal(openState)
          }}
          title={t('filtersOptionsModal.payments.title', { ns: 'common' })}
          actionButtonText={t('filtersOptionsModal.payments.actionButtonText', { ns: 'common' })}
        />
      )}
    </div>
  )
}

export default PaymentsRejectedManagement
